<template>
  <div v-if="simulation != null" class="progressbar">
    <button
      class="progressbar__before -filled"
      type="button"
      :class="{ 'is-selected': selectedRoundIndex == -1 }"
      @click="goToRound(-1)"
    >
      <lucide-icon
        v-if="scenario && scenario.showStartMessage && (scenario.startText || scenario.startTitle)"
        name="info"
        class="button-icon button-item__icon"
        @click="showStartMessage"
      />
    </button>
    <div ref="content" class="progressbar__content" :class="{ '-scrollable': segmentsWidth > contentWidth }">
      <round-segment
        v-for="{ roundIndex } of roundSegments"
        :key="roundIndex"
        ref="segmentElements"
        :class="{
          'is-selected':
            selectedRound != null && roundIndex === selectedRoundIndex
        }"
        :simulation-id="simulation.id"
        :round-index="roundIndex"
        :active="selectedRound != null && roundIndex === selectedRoundIndex"
        :width="roundSegmentWidth"
        :content-height="contentHeight"
        :hide-invisibles="hideInvisibles"
        class="progressbar__segment"
      ></round-segment>
      <div class="progressbar__line" :style="{ width: `${segmentWidth * roundSegments.length}px` }">
        <div
          class="progressbar__fill"
          :style="{ transform: `scale3d(${progress}, 1, 1)` }"
        ></div>
      </div>
    </div>
    <button
      class="progressbar__after"
      type="button"
      :class="{ 'is-selected': selectedRoundIndex == -2, '-filled': completeSuccessful }"
      @click="goToRound(-2)"
    >
      <lucide-icon
        v-if="scenario && simulation.complete && (scenario.endText || scenario.endTitle)"
        name="info"
        class="button-icon button-item__icon"
        @click="showEndMessage"
      />
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, useTemplateRef, onMounted } from "vue";
import { useResizeObserver } from '@vueuse/core';

import LucideIcon from "@core/ui/LucideIcon.vue";
import RoundSegment from "./SimulationProgressbarSegment.vue";

import { useSimulationViewStore } from "@editor/views/simulation/simulationView.store";
import { useSimulationStore } from "@editor/items/simulation/simulations.store";
import { useSimulationMessage } from "@core/ui/simulationMessage.store";

const { contentHeight, hideInvisibles = false } = defineProps<{
  contentHeight?: number;
  hideInvisibles?: boolean;
}>();

const simulationViewStore = useSimulationViewStore();

const rounds = computed(() => simulationViewStore.rounds);
const selectedRound = computed(() => simulationViewStore.selectedRound);
const simulation = computed(() => simulationViewStore.simulation);
const scenario = computed(() => simulationViewStore.scenario);
const selectedRoundIndex = computed(() => simulationViewStore.selectedRoundIndex);
const currentRoundIndex = computed(() => simulationViewStore.currentRoundIndex);

const segmentElements = useTemplateRef("segmentElements");

const progress = computed(() => {
  if (simulation.value == null) return 0;
  if (currentRoundIndex.value < 0) return 0;

  let index = currentRoundIndex.value;

  if (simulation.value.complete) {
    index += 1;
  }

  // Todo sometime: add more detailed progress

  return index / roundSegments.value.length;
});

const roundSegments = computed(() => {
  if (simulation.value == null) return [];
  const id = simulation.value.id;

  return Array.from({ length: simulation.value.maxRounds || 0 }, (v, i) => ({
    roundIndex: i,
    round: rounds.value[i],
    roundActionPlan: useSimulationStore().getActionPlan(id, i)
  }));
});

const completeSuccessful = computed(() => {
  return simulation.value?.complete && (simulation.value?.endReason == null || simulation.value?.endReason?.type == "ended");
});

function showStartMessage(mouseEvent: MouseEvent) {
  mouseEvent.preventDefault();
  mouseEvent.stopPropagation();
  if (!scenario.value) return;

  const simulationMessage = useSimulationMessage();
  simulationMessage.show({
    type: scenario.value.title,
    title: scenario.value.startTitle,
    item: {
      type: "simulationStart",
      text: scenario.value.startText || "",
      imageId: scenario.value.startImageId
    }
  });
}

function showEndMessage(mouseEvent: MouseEvent) {
  mouseEvent.preventDefault();
  mouseEvent.stopPropagation();

  if (simulation.value?.endReason?.endId != null) {
    simulationViewStore.showScenarioEndPopup({
      scenarioEndId: simulation.value.endReason.endId
    });
    return;
  }

  if (!scenario.value) return;

  const simulationMessage = useSimulationMessage();
  simulationMessage.show({
    type: scenario.value.title,
    title: scenario.value.endTitle,
    item: {
      type: "simulationEnd",
      text: scenario.value.endText || "",
      imageId: scenario.value.endImageId
    }
  });
}

function goToRound(roundIndex: number) {
  simulationViewStore.goToRound(roundIndex);
}

// ==============================
// RESIZING

const contentWidth = ref(800);
const segmentWidth = ref(25);
const roundSegmentWidth = ref(25);
const segmentsWidth = computed(() => {
  return roundSegmentWidth.value * roundSegments.value.length;
});
const content = ref<HTMLElement>();

function updateIconSpace(width?: number) {
  if (!content.value) return;
  if (width == null) {
    width = content.value.offsetWidth;
  }
  const segmentPercent = 100 / (simulation.value?.maxRounds || 1);
  contentWidth.value = Math.floor(width);
  segmentWidth.value = Math.max((width / 100) * segmentPercent, 25);
  roundSegmentWidth.value = Math.floor(segmentWidth.value);
}

watch(content, () => {
  updateIconSpace();
}, { immediate: true });

useResizeObserver(content, (entries) => {
  const entry = entries[0];
  if (entry == null) return;
  const { width } = entry.contentRect;
  updateIconSpace(width);
});


// ==============================
// FOCUSING

function focusActiveSegment(behavior: "smooth" | "instant" = "smooth") {
  if (segmentElements.value == null) return;
  const selectedRound = simulationViewStore.selectedRound;
  if (selectedRound == null) return;

  const segment = segmentElements.value[selectedRound.index];
  if (segment == null) return;

  segment.mainElement?.scrollIntoView({
    behavior: behavior,
    block: "nearest",
    inline: "center"
  });
}
onMounted(() => {
  focusActiveSegment("instant");
});

watch(selectedRoundIndex, () => {
  if (selectedRoundIndex.value < 0) return;
  focusActiveSegment("smooth");
});
</script>

<style lang="scss">
.progressbar {
  width: 100%;
  position: relative;
  display: grid;
  grid-template-columns: 2rem minmax(0, 1fr) 2rem;
  background-color: var(--color-background-deep);
  &__content {
    width: 100%;
    position: relative;
    display: flex;
    justify-content: stretch;
    overflow: hidden;
    
    &.-scrollable {
      overflow-x: auto;
      overflow-y: hidden;
    }
  }
  &__line {
    height: 3px;
    position: absolute;
    top: 0.4rem;
    left: 0;
    right: 0;
    background-color: var(--color-text-lighter);
    pointer-events: none;
  }
  &__fill {
    height: 100%;
    width: 100%;
    background-color: var(--color-action);
    transform-origin: left center;
    transition: transform 0.5s;
    transform: translate3d(0, 0, 0);
  }

  &__segment,
  &__before,
  &__after {
    background-color: transparent;
    border: none;
    box-shadow: inset 0 0 0 transparent;
    font-family: var(--font-family-ui);
    &:focus {
      outline: none;
    }
    &.is-selected {
      background-color: var(--color-action-light);
    }
  }
  &__before:focus-visible,
  &__after:focus-visible,
  &__segment.has-focus {
    transition: box-shadow 125ms ease-out;
    box-shadow: inset 0 -5px 0 var(--color-action); // , inset 0 0 0 1px var(--color-action);
  }

  &__segment {
    width: 100%;
    overflow: hidden;
    flex-grow: 0;
    flex-shrink: 1;
    min-width: 25px;
    flex-basis: 100%;
    border-left: 1px solid var(--color-border-light);
  }
  &__before {
    padding-left: 3px;
    
    &::before {
      left: 1rem;
    }
    &::after {
      left: 1rem;
    }
  }

  &__after {
    padding-right: 3px;
    border-left: 1px solid var(--color-border-light);
    &::before {
      right: 1rem;
    }
    &::after {
      right: 1rem;
    }
  }

  &__before,
  &__after {
    width: 2rem;
    position: relative;
    padding-top: 2rem;
    padding-inline: 0;
    text-align: center;

    --color: var(--color-grey);

    &.-filled {
      --color: var(--color-action);
    }

    &::before,
    &::after {
      content: "";
      display: block;
      position: absolute;
      background-color: var(--color);
      transform: translate3d(0, 0, 0);
    }

    &::before {
      top: 0.1rem;
      height: 0.8rem;
      width: 0.2rem;
    }
    &::after {
      top: 0.4rem;
      height: 3px;
      width: 1rem;
    }
    .button-icon {
      font-size: 12px;
      padding: 0;
      color: var(--color-text-medium);
      &:hover {
        color: #000;
      }
    }
    &.is-selected {
      .button:not(:hover) {
        color: var(--color-dark);
      }
      .button:hover {
        color: var(--color-action);
      }
    }
  }
}
</style>
